import { useState, useEffect, useCallback } from "react";
import { SPECIALIST_HOSTNAME } from "env-vars";

/** Matches specialist ContentController / GenerateAiContent return shape */
export interface AiContentResponse {
  /** HTML string when format is "html"; structured object when format is "json" */
  content: string | Record<string, unknown>;
  format: "html" | "json";
  metadata?: {
    model?: string;
    tokens_used?: number;
    prompt_tokens?: number;
    completion_tokens?: number;
  };
}

/** JSON content shape from specialist (e.g. OpenAiContent::InMemory mock) */
interface AiContentJsonShape {
  heading?: string;
  body?: string;
  cta_text?: string;
  cta_url?: string;
  urgency?: string;
  key_points?: string[];
  prompt_received?: string;
  [key: string]: unknown;
}

function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };
  return String(text).replace(/[&<>"']/g, (ch) => map[ch] ?? ch);
}

/**
 * Extracts a single HTML string for display from the specialist API response.
 * When format is "html", returns content as-is. When format is "json", builds
 * HTML from heading, body, key_points; CTA is only rendered as a link when
 * cta_url is a valid non-empty URL.
 */
export function extractDisplayContent(data: AiContentResponse): string {
  const { content, format } = data;
  if (format === "html" && typeof content === "string") {
    return content;
  }
  if (format === "json" && content !== null && typeof content === "object") {
    const obj = content as AiContentJsonShape;
    const parts: string[] = [];
    if (obj.heading != null && String(obj.heading).trim() !== "") {
      parts.push(`<h3>${escapeHtml(String(obj.heading))}</h3>`);
    }
    if (obj.body != null && String(obj.body).trim() !== "") {
      parts.push(`<p>${escapeHtml(String(obj.body))}</p>`);
    }
    if (Array.isArray(obj.key_points) && obj.key_points.length > 0) {
      const items = obj.key_points
        .filter((p): p is string => typeof p === "string")
        .map((p) => `<li>${escapeHtml(p)}</li>`)
        .join("");
      parts.push(`<ul>${items}</ul>`);
    }
    const ctaUrl =
      obj.cta_url != null ? String(obj.cta_url).trim() : "";
    if (ctaUrl !== "") {
      const ctaText =
        obj.cta_text != null && String(obj.cta_text).trim() !== ""
          ? escapeHtml(String(obj.cta_text))
          : escapeHtml(ctaUrl);
      parts.push(`<a href="${escapeHtml(ctaUrl)}">${ctaText}</a>`);
    }
    return parts.join("");
  }
  return "";
}

interface UseAiContentOptions {
  /** The AI prompt to send to the specialist API */
  prompt: string;
  /** Whether this content item is an AI prompt (if false, content is returned as-is) */
  isAiPrompt: boolean;
  /** Whether to skip fetching (useful for conditional rendering) */
  skip?: boolean;
}

interface UseAiContentResult {
  /** The generated HTML content (or original prompt if not an AI prompt) */
  content: string | null;
  /** Whether the content is currently being fetched */
  isLoading: boolean;
  /** Error message if the fetch failed */
  error: string | null;
  /** Function to retry fetching the content */
  retry: () => void;
}

/**
 * Hook to fetch AI-generated content from the specialist API.
 * 
 * When isAiPrompt is true, the prompt is sent to the specialist's
 * content generation endpoint and the generated HTML is returned.
 * 
 * When isAiPrompt is false, the prompt is returned as-is (it's actually HTML content).
 */
export function useAiContent({
  prompt,
  isAiPrompt,
  skip = false,
}: UseAiContentOptions): UseAiContentResult {
  const [content, setContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchAiContent = useCallback(async () => {
    // If not an AI prompt, just return the content as-is
    if (!isAiPrompt) {
      setContent(prompt);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Skip if no prompt or skip flag is set
    if (!prompt || skip) {
      setContent(null);
      setIsLoading(false);
      return;
    }

    // Get authentication credentials
    // Try auth0 id first, then fall back to questionnaire session id
    const patientIdFromAuth0 = localStorage.getItem("ciq_id");
    const patientIdFromQuestionnaire = localStorage.getItem(
      "ciq-questionnaire.sessionId"
    );
    const patientId = patientIdFromAuth0 || patientIdFromQuestionnaire;
    const idToken = localStorage.getItem("id_token");

    if (!patientId) {
      setError("Patient ID not found");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}/content/generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(idToken && { Authorization: `Bearer ${idToken}` }),
          },
          body: JSON.stringify({
            prompt,
            format: "html",
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData?.error?.message || `Failed to generate content: ${response.status}`
        );
      }

      const data: AiContentResponse = await response.json();
      setContent(extractDisplayContent(data));
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to generate AI content";
      setError(errorMessage);
      setContent(null);
      console.error("Failed to fetch AI content:", err);
    } finally {
      setIsLoading(false);
    }
  }, [prompt, isAiPrompt, skip]);

  // Fetch content on mount and when dependencies change
  useEffect(() => {
    fetchAiContent();
  }, [fetchAiContent, retryCount]);

  const retry = useCallback(() => {
    setRetryCount((prev) => prev + 1);
  }, []);

  return {
    content,
    isLoading,
    error,
    retry,
  };
}
